// Mock data for the v3 STUDENT dashboard. Demo-local, swap for real API data later.
// The structural model is shared with the teacher side (TeacherDashboardV3/data):
// two subjects, four domains each, seven levels (teacher-side "bands") per domain.
// Inside a level the student works through a fixed sequence of parts: the learn
// cycle from the teacher Guide page, with an Overview in front.

import { BAND_GUIDES } from '../../TeacherDashboardV3/data/bandGuides';
import { DOMAINS } from '../../TeacherDashboardV3/data/mockData';
import { allAssignments, shortDate } from '../../TeacherDashboardV3/data/unitPlan';
import { conceptsFor } from './levelConcepts';
import { bandRange } from '../../utils/satScoreBands';

export { conceptsFor } from './levelConcepts';

export { DOMAINS, DOMAIN_GUIDES, domainOf, subjectLabel } from '../../TeacherDashboardV3/data/mockData';

export const MAX_LEVEL = 7;

// Reading domains with NO live Level 1 content (Sandy 2026-08-08): Info &
// Ideas, Craft & Structure and Expression of Ideas place students at 2 and
// have nothing to play at 1 — their ladders start at Level 2 and Level 1 is
// never clickable or deep-linkable. Conventions and every math domain do
// have a real Level 1.
export const NO_LEVEL_1 = new Set(['info', 'craft', 'expression']);
export const minLevelOf = (domainKey) => (NO_LEVEL_1.has(domainKey) ? 2 : 1);

// Domains with a playable band loop: the StudentDash ReadingSessionPage TEST MODE
// (local content under public/test-content/<sessionId>/band-N.json, nothing saved).
// This dashboard is the launcher for it — maps our domain key to the session id.
export const TEST_SESSION_DOMAINS = { expression: 'expression-of-ideas' };
export const TEST_SESSION_MIN_LEVEL = 2; // band-1 content doesn't exist (Skills Insight bands run 2-7)

// Domains LIVE in the engine (LearnDomains registrations): a real placed
// student's Start launches the actual DomainLearnSession loop at /session/<id>
// (saved progress, real grading). The other reading domains stay toast-only
// until their content is wired.
export const ENGINE_SESSION_DOMAINS = {
  algebra: 'algebra',
  psda: 'problem-solving-and-data-analysis',
  advanced: 'advanced-math',
  geotrig: 'geometry-and-trigonometry',
  expression: 'expression-of-ideas',
  conventions: 'standard-english-conventions',
  // 2026-08-08: the last two reading domains. Both have full LearnDomains
  // content (5 concepts each) — they were simply never registered, so Start
  // had no engine URL and silently fell back to non-existent test content.
  info: 'information-and-ideas',
  craft: 'craft-and-structure',
};

export function testSessionUrl(domainKey, level) {
  const id = TEST_SESSION_DOMAINS[domainKey];
  if (!id || level < TEST_SESSION_MIN_LEVEL || level > MAX_LEVEL) return null;
  return `/session/${id}?band=${level}&test=1`;
}

// Real engine session for a placed student — the engine resumes wherever their
// DOMAIN# row says they are, so no band/part params are needed.
export function engineSessionUrl(domainKey) {
  const id = ENGINE_SESSION_DOMAINS[domainKey];
  return id ? `/session/${id}` : null;
}

// Reverse lookup for the session page's exit button: session id -> dashboard
// domain page. Covers both the engine domains and the local test-content ones.
export function dashboardPathForSessionId(sessionId) {
  const entry = Object.entries(ENGINE_SESSION_DOMAINS).find(([, id]) => id === sessionId)
    || Object.entries(TEST_SESSION_DOMAINS).find(([, id]) => id === sessionId);
  if (!entry) return null;
  const key = entry[0];
  const subject = DOMAINS.reading.some((d) => d.key === key) ? 'reading' : 'math';
  return `/student-dashboard/${subject}/${key}`;
}

// The parts of a level, in order, SEGMENTED PER CONCEPT (Sandy 2026-07-14):
// each concept group opens with its own short overview (a quick check-in on
// that concept from the last level it appeared in), then warm-up -> lesson ->
// practice. Each group is ONE ASSIGNMENT. The level closes with the final
// assignment: mixed practice across all concepts, then the mastery check
// (>= 80%, cumulative) behind a 48-hour timer.
// `part` in STUDENT.progress is an index into partsFor(domainKey, level).
export const CHECK_TIMER = '48:00:00';

export function partsFor(domainKey, level) {
  const concepts = conceptsFor(domainKey, level);
  const parts = [];
  concepts.forEach((con, i) => {
    parts.push({
      key: `overview-${i}`, label: 'Overview', concept: con.name, group: i,
      blurb: con.first
        ? `What ${con.name} is and why it matters on the test`
        : `A quick check-in on ${con.name}: what you remember from last time`,
    });
    parts.push({
      key: `warmup-${i}`, label: 'Warm-up', concept: con.name, group: i,
      blurb: `A few questions on ${con.name} before any teaching`,
    });
    parts.push({
      key: `lesson-${i}`, label: con.name, concept: con.name, group: i,
      blurb: con.first
        ? `How ${con.name} works: a worked example, step by step, mistakes included`
        : `What's new about ${con.name} at this level`,
    });
    parts.push({
      key: `practice-${i}`, label: 'Practice', concept: con.name, group: i,
      blurb: `Your turn: questions on ${con.name}`,
    });
  });
  parts.push({ key: 'mixed', label: 'Mixed practice', group: concepts.length, blurb: "All of this level's concepts, shuffled together" });
  parts.push({ key: 'check', label: 'Mastery check', group: concepts.length, blurb: 'Score 80% or better to level up', timer: CHECK_TIMER });
  return parts;
}

// One demo student. `level` = the level they're on in that domain (1-7);
// `part` = index into partsFor(domainKey, level) of the part they're currently on.
export const STUDENT = {
  firstName: 'Maya',
  streakDays: 5,
  // The class unit: domain + the teacher's pacing window (week N of total).
  // Goal per the curriculum: finish the level you're on before the unit ends.
  active: { subject: 'reading', domain: 'expression', week: 2, total: 4 },
  progress: {
    // math
    algebra:  { level: 4, part: 2 },
    psda:     { level: 3, part: 5 },
    advanced: { level: 2, part: 0 },
    geotrig:  { level: 5, part: 3 },
    // reading & writing
    info:        { level: 3, part: 4 },
    craft:       { level: 2, part: 1 },
    conventions: { level: 4, part: 6 },
    expression:  { level: 2, part: 3 },
  },
};

// Student-facing level title + skills, reusing the teacher band guides
// (titles like "Fundamentals: signposts & student notes" read fine for students;
// the teacher-voiced `learn`/`teach` prose stays teacher-side).
export function levelInfo(domainKey, level) {
  const band = (BAND_GUIDES[domainKey] || [])[level - 1];
  return { title: band?.title || `Level ${level}`, skills: band?.skills || [] };
}

// ---- Placement (mock) ----
// Before the diagnostic places a student, a domain has no level: progressOf
// returns { level: null, part: 0 } and the UI shows its placement empty states
// (dashed "Not placed yet" badge on domain cards, centered diagnostic CTA on the
// domain page). The demo student stays fully placed — UNPLACED remains false;
// preview the states with ?unplaced=1 on the subject/domain pages.
export const UNPLACED = false;

export const progressOf = (domainKey, unplaced = UNPLACED) =>
  unplaced ? { level: null, part: 0 } : STUDENT.progress[domainKey] || { level: 1, part: 0 };

// ---- Class-unit pacing (one derivation, shown consistently on every page) ----
// Expected parts by now = the unit window fraction applied to this level's parts.
export function unitStatus() {
  const { subject, domain, week, total } = STUDENT.active;
  const prog = progressOf(domain);
  const parts = partsFor(domain, prog.level);
  const expectedParts = Math.min(parts.length, Math.ceil(parts.length * (week / total)));
  const weeksLeft = total - week;
  return {
    subject,
    domain,
    domainName: DOMAINS[subject].find((d) => d.key === domain).full,
    level: prog.level,
    week,
    total,
    weeksLeft,
    doneParts: prog.part,
    totalParts: parts.length,
    expectedParts,
    onTrack: prog.part >= expectedParts,
  };
}

// ---- What's due next (the unit plan, student-side) ----
// The teacher's weekly assignments become one stated line everywhere a domain
// shows up: "Rhetorical Synthesis due Fri, Jul 17". Derived from the same
// plan model the teacher edits (default split here; per-class plan via API
// later). The unit covers the level the student is on in that domain.
function activeUnit() {
  const { subject, domain, week, total } = STUDENT.active;
  return { subject, domain, week, total, targetBand: progressOf(domain).level || 2 };
}

// The class unit is a fixed sequence of assignment TYPES (Sandy 2026-07-16):
// Lesson, Lesson, Mixed Practice, Skill Check. Always this order, and each is
// its own item. Mixed Practice and Skill Check are NEVER combined into one.
// Due on successive Fridays across the unit window, the first already behind us
// so the to-do list shows a natural past / next spread.
const UNIT_TYPES = ['Lesson', 'Lesson', 'Mixed Practice', 'Skill Check'];

export function unitTodos() {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const fri = new Date(now);
  fri.setDate(now.getDate() + ((5 - now.getDay() + 7) % 7)); // this week's Friday
  return UNIT_TYPES.map((type, i) => {
    const due = new Date(fri);
    due.setDate(fri.getDate() + (i - 1) * 7); // i=0 last Friday, i=1 this Friday, ...
    return {
      key: 't' + i,
      type,                       // 'Lesson' | 'Mixed Practice' | 'Skill Check'
      due,
      daysLeft: Math.ceil((due - now) / 86400000),
      dueLong: shortDate(due),    // 'Fri, Jul 17'
      dueDay: due.toLocaleDateString('en-US', { weekday: 'long' }), // 'Friday'
      past: due < todayStart,
    };
  });
}

// What's due next: the first assignment not yet past. One type, never combined.
export function unitDue() {
  const todos = unitTodos();
  const current = todos.find((t) => !t.past) || todos[todos.length - 1];
  if (!current) return null;
  return {
    subject: STUDENT.active.subject,
    domain: STUDENT.active.domain,
    key: current.key,
    type: current.type,               // 'Lesson' | 'Mixed Practice' | 'Skill Check'
    label: current.type,              // the single type, shown as-is
    action: `finish your ${current.type.toLowerCase()}`,
    due: current.due,
    daysLeft: current.daysLeft,
    dueLong: current.dueLong,
    dueDay: current.dueDay,
  };
}

// All of the unit's assignments (for the road's due markers). Empty when the
// domain isn't the class unit.
export function unitAssignmentsFor(domainKey) {
  if (STUDENT.active.domain !== domainKey) return [];
  return allAssignments(activeUnit(), null);
}

// Where each assignment ends on a level's road: the part index that completes
// it. Concept assignments end at their Practice; the final one at the check.
export function assignmentPartIndex(domainKey, level, assignmentKey) {
  const parts = partsFor(domainKey, level);
  if (assignmentKey === 'mixed') return parts.findIndex((p) => p.key === 'mixed');
  if (assignmentKey === 'check') return parts.findIndex((p) => p.key === 'check');
  const i = parseInt(assignmentKey.slice(1), 10);
  return parts.findIndex((p) => p.key === `practice-${i}`);
}

// This week's activity (mock; real numbers come from the same telemetry the
// teacher's minutes column uses).
export const WEEK_RECAP = {
  partsDone: 5,
  minutes: 84,
  levelUps: [{ domain: 'craft', subject: 'reading', from: 1, to: 2 }],
};

// ---- Expected score range (mock derivation) ----
// Section-score range per level: College Board's Skills Insight bands
// (utils/satScoreBands, per subject; was a single older table until 2026-10-06).

// A section's expected range = the band range at the student's average level
// across the subject's four domains. Real scoring comes from the backend later.
export function sectionScoreRange(subject) {
  const keys = DOMAINS[subject].map((d) => d.key);
  const avg = keys.reduce((s, k) => s + progressOf(k).level, 0) / keys.length;
  const band = Math.max(1, Math.min(7, Math.round(avg)));
  return bandRange(subject, band);
}

export function totalScoreRange() {
  const r = sectionScoreRange('reading');
  const m = sectionScoreRange('math');
  return [r[0] + m[0], r[1] + m[1]];
}
